import {
  ACESFilmicToneMapping,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
} from 'three';
import { TICK_DT } from '../movement/constants';
import type { PostFX, Quality } from './PostFX';

type UpdateFn = (dt: number) => void;
type RenderFn = (alpha: number, frameDt: number) => void;

/**
 * Renderer + fixed-timestep game loop. Simulation always steps at TICK_DT
 * (air-strafe physics is tickrate-sensitive); rendering runs at display rate
 * and receives the interpolation factor `alpha` for smoothing.
 */
export class Engine {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera: PerspectiveCamera;

  private readonly updateFns: UpdateFn[] = [];
  private readonly renderFns: RenderFn[] = [];
  private accumulator = 0;
  private lastTime = 0;
  private postFx: PostFX | null = null;
  private pendingQuality: Quality = 'low';
  /** When set, force a bloom-only composer even on 'low' (glowy maps opt in). */
  private bloomFloor = false;
  /** Simulation time scale (1 = real time). The slow-mo trainer drops this. */
  private timeScale = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    // Lighter exposure tweak retained — cheap, no per-frame cost.
    this.renderer.toneMappingExposure = 0.9;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;

    // Far plane sized for Hammer units: maps span thousands of units.
    this.camera = new PerspectiveCamera(90, window.innerWidth / window.innerHeight, 1, 50000);
    this.resize(); // normalize to the visual viewport (differs on iOS)

    window.addEventListener('resize', () => this.resize());
    // iOS (spec 0029): rotation and address-bar collapse resize the *visual*
    // viewport without a reliable window resize; track both so the canvas
    // never letterboxes. On desktop these coincide with window resize.
    window.addEventListener('orientationchange', () => this.resize());
    window.visualViewport?.addEventListener('resize', () => this.resize());
  }

  /**
   * The size everything renders at. On iOS the *visual* viewport is the honest
   * one (the address bar overlays the window), so it — not innerWidth/Height —
   * is the single source of truth for the camera, renderer and composer.
   */
  private viewportSize(): { width: number; height: number } {
    return {
      width: window.visualViewport?.width ?? window.innerWidth,
      height: window.visualViewport?.height ?? window.innerHeight,
    };
  }

  private resize(): void {
    const { width, height } = this.viewportSize();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.postFx?.setSize(width, height);
  }

  /**
   * Set the render quality. `low` (default) bypasses post-processing entirely
   * and renders direct with built-in MSAA — the smoothest, lightest path, which
   * is what a browser game on modest hardware wants. medium/high/ultra add the
   * post-processing composer (SMAA, then bloom, then GTAO) for capable GPUs.
   *
   * The composer code is loaded on demand (dynamic import): the default `low`
   * path never downloads or parses it, keeping the initial bundle small. While
   * the chunk loads the render loop falls back to direct rendering.
   */
  setQuality(quality: Quality): void {
    this.pendingQuality = quality;
    this.applyPixelRatio(quality);
    if (quality === 'low' && !this.bloomFloor) {
      // Direct render path — composer chunk never needed. Free its render
      // targets first: dropping the reference alone leaks them on the GPU.
      this.postFx?.dispose();
      this.postFx = null;
      return;
    }
    void this.loadPostFx(quality);
  }

  /**
   * Opt a glowy map into bloom even on the 'low' preset (a cheap bloom-only
   * composer). Maps set this on load; clearing it returns 'low' to direct render.
   */
  setBloomFloor(on: boolean): void {
    if (on === this.bloomFloor) {
      return;
    }
    this.bloomFloor = on;
    this.setQuality(this.pendingQuality); // re-apply with the floor in mind
  }

  private async loadPostFx(quality: Quality): Promise<void> {
    const { PostFX } = await import('./PostFX');
    if (this.pendingQuality !== quality) {
      return; // superseded by a newer setQuality call
    }
    // Only 'low' reaches here with the floor on → it wants the bloom-only chain.
    const bloomOnly = quality === 'low';
    if (this.postFx) {
      this.postFx.setBloomOnly(bloomOnly);
      this.postFx.setQuality(quality);
    } else {
      this.postFx = new PostFX(this.renderer, this.scene, this.camera, quality, bloomOnly);
    }
    const { width, height } = this.viewportSize();
    this.postFx.setSize(width, height);
  }

  /** Render resolution scales with quality — the cheapest perf lever. */
  private applyPixelRatio(quality: Quality): void {
    const cap = quality === 'low' ? 1 : quality === 'medium' ? 1.5 : 2;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, cap));
  }

  /**
   * Scale the rate the fixed-tick accumulator fills (1 = real time). The physics
   * step size never changes — only how much sim time each real frame buys — so
   * tickrate-sensitive air-strafe math is unaffected. Used by the slow-mo trainer.
   */
  setTimeScale(scale: number): void {
    this.timeScale = Math.max(0, scale);
  }

  /**
   * High-speed vignette boost (0 at rest). No-op on the direct-render path
   * ('low' without a bloom floor) — there the FOV kick alone carries the feel.
   */
  setSpeedVignette(extra: number): void {
    this.postFx?.setSpeedVignette(extra);
  }

  onUpdate(fn: UpdateFn): void {
    this.updateFns.push(fn);
  }

  onRender(fn: RenderFn): void {
    this.renderFns.push(fn);
  }

  start(): void {
    this.lastTime = performance.now();
    const frame = (now: number) => {
      // Clamp to avoid a spiral of death after tab-switch pauses.
      const frameDt = Math.min((now - this.lastTime) / 1000, 0.1);
      this.lastTime = now;
      // timeScale slows/speeds how fast sim time accrues; the tick size is fixed.
      this.accumulator += frameDt * this.timeScale;

      while (this.accumulator >= TICK_DT) {
        for (const fn of this.updateFns) {
          fn(TICK_DT);
        }
        this.accumulator -= TICK_DT;
      }

      const alpha = this.accumulator / TICK_DT;
      for (const fn of this.renderFns) {
        fn(alpha, frameDt);
      }
      if (this.postFx) {
        this.postFx.render();
      } else {
        this.renderer.render(this.scene, this.camera);
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }
}
